import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { BTreeVisualizer } from "./btree-visualizer";
import { RecommendationReport } from "./recommendation-report";
import { 
  Database, 
  HardDrive, 
  Cpu, 
  MemoryStick, 
  Network,
  AlertTriangle,
  Play,
  Pause,
  RotateCcw,
  Activity,
  TrendingUp,
  Shield,
  Zap,
  Clock
} from "lucide-react";

interface DiagnosticMetrics {
  cpuUsage: number;
  memoryUsage: number;
  diskIOPS: number;
  diskLatency: number;
  networkThroughput: number;
  waitStats: {
    type: string;
    waitTime: number;
    percentage: number;
  }[];
  blockingChains: number;
  deadlocks: number;
  queryDuration: number;
}

export function DiagnosticSuite() {
  const [isRunning, setIsRunning] = useState(false);
  const [workloadIntensity, setWorkloadIntensity] = useState([50]);
  const [ioPattern, setIOPattern] = useState([70]); // % sequential vs random
  const [concurrency, setConcurrency] = useState([4]);
  
  const [metrics, setMetrics] = useState<DiagnosticMetrics>({
    cpuUsage: 45,
    memoryUsage: 62,
    diskIOPS: 1250,
    diskLatency: 8.5,
    networkThroughput: 125.3,
    waitStats: [
      { type: "CXPACKET", waitTime: 2350, percentage: 35 },
      { type: "PAGEIOLATCH_SH", waitTime: 1800, percentage: 27 },
      { type: "LCK_M_S", waitTime: 950, percentage: 18 },
      { type: "ASYNC_NETWORK_IO", waitTime: 680, percentage: 12 },
      { type: "WRITELOG", waitTime: 420, percentage: 8 }
    ],
    blockingChains: 3,
    deadlocks: 0,
    queryDuration: 156
  });

  const startSimulation = () => {
    setIsRunning(true);
    // Simulate real-time metrics updates
    const interval = setInterval(() => {
      setMetrics(prev => ({
        ...prev,
        cpuUsage: Math.max(10, Math.min(95, prev.cpuUsage + (Math.random() - 0.5) * 10)),
        memoryUsage: Math.max(20, Math.min(90, prev.memoryUsage + (Math.random() - 0.5) * 5)),
        diskIOPS: Math.max(100, Math.min(5000, prev.diskIOPS + (Math.random() - 0.5) * 200)),
        diskLatency: Math.max(1, Math.min(50, prev.diskLatency + (Math.random() - 0.5) * 2)),
        blockingChains: Math.max(0, Math.min(10, prev.blockingChains + Math.floor((Math.random() - 0.7) * 2))),
        queryDuration: Math.max(10, Math.min(1000, prev.queryDuration + (Math.random() - 0.5) * 50))
      }));
    }, 1000);

    setTimeout(() => {
      clearInterval(interval);
      if (isRunning) setIsRunning(false);
    }, 30000); // Auto-stop after 30 seconds
  };

  const stopSimulation = () => {
    setIsRunning(false);
  };

  const resetMetrics = () => {
    setMetrics({
      cpuUsage: 45,
      memoryUsage: 62,
      diskIOPS: 1250,
      diskLatency: 8.5,
      networkThroughput: 125.3,
      waitStats: [
        { type: "CXPACKET", waitTime: 2350, percentage: 35 },
        { type: "PAGEIOLATCH_SH", waitTime: 1800, percentage: 27 },
        { type: "LCK_M_S", waitTime: 950, percentage: 18 },
        { type: "ASYNC_NETWORK_IO", waitTime: 680, percentage: 12 },
        { type: "WRITELOG", waitTime: 420, percentage: 8 }
      ],
      blockingChains: 3,
      deadlocks: 0,
      queryDuration: 156
    });
  };

  return (
    <div className="space-y-6">
      {/* Control Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            SQL Performance Diagnostic Suite
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Workload Controls */}
            <div className="space-y-3">
              <label className="text-sm font-medium">Workload Intensity</label>
              <Slider
                value={workloadIntensity}
                onValueChange={setWorkloadIntensity}
                max={100}
                step={1}
                className="w-full"
              />
              <div className="text-xs text-github-muted">{workloadIntensity[0]}% intensity</div>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium">I/O Pattern</label>
              <Slider
                value={ioPattern}
                onValueChange={setIOPattern}
                max={100}
                step={1}
                className="w-full"
              />
              <div className="text-xs text-github-muted">{ioPattern[0]}% sequential</div>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium">Concurrency</label>
              <Slider
                value={concurrency}
                onValueChange={setConcurrency}
                max={32}
                min={1}
                step={1}
                className="w-full"
              />
              <div className="text-xs text-github-muted">{concurrency[0]} threads</div>
            </div>

            {/* Control Buttons */}
            <div className="flex flex-col gap-2">
              <Button 
                onClick={isRunning ? stopSimulation : startSimulation}
                className={`${isRunning ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white`}
              >
                {isRunning ? (
                  <>
                    <Pause className="w-4 h-4 mr-2" />
                    Stop
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Start
                  </>
                )}
              </Button>
              <Button onClick={resetMetrics} variant="outline">
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Real-time Metrics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* CPU Usage */}
        <Card className="relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <Cpu className="w-8 h-8 text-shiftleft-600 mb-2" />
                <div className="text-2xl font-bold">{metrics.cpuUsage.toFixed(1)}%</div>
                <div className="text-sm text-github-muted">CPU Usage</div>
              </div>
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                  <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="4" fill="transparent" className="text-gray-200" />
                  <circle 
                    cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="4" fill="transparent" 
                    strokeDasharray={`${metrics.cpuUsage * 1.76} 176`}
                    className="text-shiftleft-600"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-bold">{Math.round(metrics.cpuUsage)}%</span>
                </div>
              </div>
            </div>
            {isRunning && (
              <div className="absolute top-2 right-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Memory Usage */}
        <Card className="relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <MemoryStick className="w-8 h-8 text-green-600 mb-2" />
                <div className="text-2xl font-bold">{metrics.memoryUsage.toFixed(1)}%</div>
                <div className="text-sm text-github-muted">Memory Usage</div>
              </div>
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                  <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="4" fill="transparent" className="text-gray-200" />
                  <circle 
                    cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="4" fill="transparent" 
                    strokeDasharray={`${metrics.memoryUsage * 1.76} 176`}
                    className="text-green-600"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-bold">{Math.round(metrics.memoryUsage)}%</span>
                </div>
              </div>
            </div>
            {isRunning && (
              <div className="absolute top-2 right-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Disk IOPS */}
        <Card className="relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <HardDrive className="w-8 h-8 text-purple-600 mb-2" />
                <div className="text-2xl font-bold">{metrics.diskIOPS.toLocaleString()}</div>
                <div className="text-sm text-github-muted">Disk IOPS</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-purple-600">{metrics.diskLatency.toFixed(1)}ms</div>
                <div className="text-xs text-github-muted">Latency</div>
              </div>
            </div>
            {isRunning && (
              <div className="absolute top-2 right-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Network & Blocking */}
        <Card className="relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <Network className="w-8 h-8 text-orange-600 mb-2" />
                <div className="text-2xl font-bold">{metrics.networkThroughput.toFixed(1)}</div>
                <div className="text-sm text-github-muted">MB/s Network</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-red-600">{metrics.blockingChains}</div>
                <div className="text-xs text-github-muted">Blocking Chains</div>
                {metrics.deadlocks > 0 && (
                  <Badge variant="destructive" className="mt-1">
                    {metrics.deadlocks} Deadlocks
                  </Badge>
                )}
              </div>
            </div>
            {isRunning && (
              <div className="absolute top-2 right-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analytics */}
      <Tabs defaultValue="wait-stats" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="wait-stats">Wait Stats</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="blocking">Blocking</TabsTrigger>
          <TabsTrigger value="disk-sim">Disk I/O</TabsTrigger>
          <TabsTrigger value="btree">B-Tree Analysis</TabsTrigger>
          <TabsTrigger value="recommendations">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="wait-stats" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                SQL Server Wait Statistics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {metrics.waitStats.map((wait, index) => (
                  <div key={wait.type} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{wait.type}</Badge>
                        <span className="text-sm text-github-muted">{wait.waitTime}ms total</span>
                      </div>
                      <span className="font-semibold">{wait.percentage}%</span>
                    </div>
                    <Progress value={wait.percentage} className="h-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Query Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Average Duration</span>
                    <span className="font-semibold">{metrics.queryDuration}ms</span>
                  </div>
                  <Progress value={(metrics.queryDuration / 1000) * 100} className="h-2" />
                  
                  <div className="pt-4 border-t">
                    <div className="text-sm text-github-muted mb-2">Performance Grade</div>
                    <Badge className={`${
                      metrics.queryDuration < 50 ? 'bg-green-100 text-green-800' :
                      metrics.queryDuration < 200 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {metrics.queryDuration < 50 ? 'Excellent (A)' :
                       metrics.queryDuration < 200 ? 'Good (B)' : 'Needs Attention (C)'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  System Health
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Overall Health Score</span>
                    <span className="font-semibold text-green-600">
                      {(100 - (metrics.cpuUsage * 0.3 + metrics.memoryUsage * 0.3 + metrics.blockingChains * 5)).toFixed(0)}%
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Resource Utilization</span>
                      <span className={metrics.cpuUsage > 80 || metrics.memoryUsage > 80 ? 'text-red-600' : 'text-green-600'}>
                        {metrics.cpuUsage > 80 || metrics.memoryUsage > 80 ? 'High' : 'Normal'}
                      </span>
                    </div>
                    
                    <div className="flex justify-between text-sm">
                      <span>Blocking Issues</span>
                      <span className={metrics.blockingChains > 5 ? 'text-red-600' : 'text-green-600'}>
                        {metrics.blockingChains > 5 ? 'Critical' : 'Normal'}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="blocking" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Blocking Chain Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="font-medium text-yellow-800 mb-2">Active Blocking Chains: {metrics.blockingChains}</div>
                  <div className="text-sm text-yellow-700">
                    Simulated blocking sessions detected. In a real environment, use sp_who2 or 
                    sys.dm_exec_requests to identify root blockers.
                  </div>
                </div>
                
                {metrics.blockingChains > 0 && (
                  <div className="space-y-2">
                    {Array.from({ length: Math.min(metrics.blockingChains, 3) }, (_, i) => (
                      <div key={i} className="flex items-center gap-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {52 + i}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">Session {52 + i} blocking Session {53 + i}</div>
                          <div className="text-sm text-gray-600">Wait Type: LCK_M_X | Duration: {(i + 1) * 15}s</div>
                        </div>
                        <Badge variant="destructive">Active</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="disk-sim" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HardDrive className="w-5 h-5" />
                DiskSpd I/O Simulation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-4 bg-shiftleft-50 border border-shiftleft-200 rounded-lg">
                    <div className="font-medium text-shiftleft-800 mb-2">Current I/O Pattern</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>IOPS:</span>
                        <span className="font-mono">{metrics.diskIOPS.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Latency:</span>
                        <span className="font-mono">{metrics.diskLatency.toFixed(1)}ms</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Pattern:</span>
                        <span className="font-mono">{ioPattern[0]}% Sequential</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Threads:</span>
                        <span className="font-mono">{concurrency[0]}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="font-medium text-green-800 mb-2">Performance Assessment</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Throughput Grade:</span>
                        <Badge className={`${
                          metrics.diskIOPS > 2000 ? 'bg-green-100 text-green-800' :
                          metrics.diskIOPS > 1000 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {metrics.diskIOPS > 2000 ? 'Excellent' :
                           metrics.diskIOPS > 1000 ? 'Good' : 'Poor'}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Latency Grade:</span>
                        <Badge className={`${
                          metrics.diskLatency < 10 ? 'bg-green-100 text-green-800' :
                          metrics.diskLatency < 20 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {metrics.diskLatency < 10 ? 'Excellent' :
                           metrics.diskLatency < 20 ? 'Good' : 'Poor'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="btree" className="space-y-4">
          <BTreeVisualizer />
        </TabsContent>

        <TabsContent value="recommendations" className="space-y-4">
          <RecommendationReport />
        </TabsContent>
      </Tabs>
    </div>
  );
}